import { Template } from '@store/user/user.model';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import { ClearAllPremium, GetLookUps, SavedPremiumLayerLookups, SetPremiumLayer } from '@store/search-board/search-board.actions';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import { combineLatest, Observable, of } from 'rxjs';
import { LookUpData } from '@store/search-board/search-board.model';
import { clearAllPremium, savedPremiumLayerLookups, selectLookupsByFieldCode } from '@store/search-board/search-board.selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FieldCode } from '@root/search/models/display-fields.constant';
import {
  InfoCards,
  PremiumMapSearchFieldConstants,
  PremiumSearchLabelStyleClassByCode
} from '@root/search/models/premium-map-searchfield.constants';
import { SearchService } from '@root/search/services/search.service';
import { LayerInfo } from '@root/search/models/map.models';
import { selectMapLayer } from '@store/map-layers.selector';
import { map, switchMap, take } from 'rxjs/operators';
import { PREMIUM_MAP_LAYER_NONE } from '@root/search/models/map.constants';
import { selectIsMobile } from '@store/user/user.selector';
import { MixpanelService } from '@shared/services/mixpanel.service';

@UntilDestroy()
@Component({
  selector: 'rlst-premium-map',
  templateUrl: './premium-map.component.html',
  styleUrls: ['./premium-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PremiumMapComponent implements OnInit, AfterViewInit {
  @Input() disabled = false;
  @Input() template: Template;
  @Input() disabledMessage: string;
  @Output() toggleVal: EventEmitter<any> = new EventEmitter<void>();
  @ViewChildren('lookupItems') lookupItems: QueryList<ElementRef>;
  public lookups: Array<any>;
  public premiumMapLabel: string = 'Premium Map';
  public selectedPremiumDataElement: any = undefined;
  public premSearchFieldOptions: any; // not data element
  public isToggleAllChecked: boolean = false;
  public popoverHtml: string;
  private premiumLayers: Array<LayerInfo>;
  private noOfLookupsSelected: number = 0;
  private selectedLayerFiledCode: string;
  public isClearAllPremium: boolean;
  public isMobile$: Observable<boolean>;

  constructor(private readonly cdr: ChangeDetectorRef,
              readonly store$: Store<AppState>,
              public searchService: SearchService,
              private readonly mixpanelService: MixpanelService,
  ) {
  }


  ngOnInit(): void {
    this.isMobile$ = this.store$.pipe(select(selectIsMobile));
    this.store$
      .pipe(
        untilDestroyed(this),
        select(selectMapLayer),
        map((layer) => {
          this.premiumLayers = layer.availableLayers;
          let layerHasBeenChanged = false;

          if (this.selectedLayerFiledCode !== layer.selectedMapLayer.fieldCode) {
            this.selectedLayerFiledCode = layer.selectedMapLayer.fieldCode;
            layerHasBeenChanged = true;
          }

          if (this.selectedPremiumDataElement &&
            layer.selectedMapLayer.fieldCode &&
            this.selectedPremiumDataElement.fieldCode !== layer.selectedMapLayer.fieldCode) {
            this.selectedPremiumDataElement = this.template.dataElementList
              .find((dataElement) => dataElement.fieldCode === layer.selectedMapLayer.fieldCode);
            this.loadLookups();
          } else if (!this.selectedPremiumDataElement && (layer.selectedMapLayer.layerId !== 'select_none')) {
            this.selectedPremiumDataElement = this.template.dataElementList
              .find((dataElement) => dataElement.fieldCode === layer.selectedMapLayer.fieldCode);
            this.loadLookups();
          } else if (layer.selectedMapLayer.layerId === 'select_none') {
            this.selectedPremiumDataElement = undefined;
            this.lookups = [];
          }

          return layerHasBeenChanged;
        }),
        switchMap((layerHasBeenChanged) => {
          return combineLatest([
            this.selectedPremiumDataElement
              ? this.store$.pipe(select(selectLookupsByFieldCode, (this.selectedPremiumDataElement.fieldCode as FieldCode)))
              : of(null),
            of(layerHasBeenChanged)
          ]);
        })
      )
      .subscribe(([lookups, layerHasBeenChanged]) => {
        if (!lookups) {
          this.cdr.markForCheck();
          return;
        }

        if (lookups && (layerHasBeenChanged || !this.lookups)) {
          this.store$.dispatch(new ClearAllPremium(false));
          this.lookups = [...lookups.map((item: LookUpData, lookUpIndex: number) => {
            return {
              code: item.code,
              label: item.label,
              selected: false,
              cssClass: JSON.parse(PremiumMapSearchFieldConstants[this.selectedPremiumDataElement.fieldCode])[lookUpIndex],
              labelStyleClass: PremiumSearchLabelStyleClassByCode[item.code],
              lookUpIndex
            };
          })];
          this.cdr.markForCheck();
        }
      });

    this.store$.pipe(untilDestroyed(this), select(clearAllPremium)).subscribe((response) => {
      this.isClearAllPremium = response;
      if (this.isClearAllPremium) {
        this.loadLookups();
      }
      this.cdr.markForCheck();
    });
  }

  public ngAfterViewInit() {
    this.store$.pipe(select(savedPremiumLayerLookups)).pipe(untilDestroyed(this), take(1)).subscribe((savedLookups) => {
      if (savedLookups?.length) {
        savedLookups.forEach((element: any, index: number) => {
          if (element.selected) {
            this.lookupItems.forEach((item, itemIndex) => {
              if (itemIndex === index) {
                const ele = item.nativeElement as HTMLElement;
                ele.click();
              }
            });
          }
        });
      }
    });
  }

  public loadLookups(): void {
    if (!this.selectedPremiumDataElement) {
      this.store$.dispatch(new SetPremiumLayer(PREMIUM_MAP_LAYER_NONE));
      return;
    }

    const selectedLayer = this.premiumLayers.find((item) => item.fieldCode === this.selectedPremiumDataElement.fieldCode);

    this.noOfLookupsSelected = 0;
    this.isToggleAllChecked = false;
    this.store$.dispatch(new SetPremiumLayer(selectedLayer));
    this.popoverHtml = InfoCards[this.selectedPremiumDataElement.fieldCode];
    this.lookups = null;
    this.store$.dispatch(new GetLookUps([this.selectedPremiumDataElement]));
    this.premSearchFieldOptions = {};
    this.toggleVal.emit(this.premSearchFieldOptions);
    this.cdr.markForCheck();
  }

  public setSelectedPremiumLayer(element: any): void {
    this.selectedPremiumDataElement = element;

    this.mixpanelService.track('Search: Premium Map - ' + element.label + ' layer');

    this.loadLookups();
    this.cdr.markForCheck();
  }

  public togglePremiumOption(event: any, option: LookUpData, isToggledSingle: boolean) {
    event.target.checked ? this.noOfLookupsSelected++ : this.noOfLookupsSelected--;
    this.isToggleAllChecked = this.noOfLookupsSelected === this.lookups.length;
    const premSearchField = {
      'fieldCode': this.selectedPremiumDataElement.fieldCode,
    };
    if (this.selectedPremiumDataElement.fieldCode === 'PREM_BUILDING_TYPE' ||
      this.selectedPremiumDataElement.fieldCode === 'PREM_COUNTY_LAND_USE_CD') {

      const index = PremiumMapSearchFieldConstants.INDEX_ONE;
      const operatorValueIsItem = {
        'operatorLabel': this.selectedPremiumDataElement.operatorValueList[index]?.operatorLabel,
        'operator': this.selectedPremiumDataElement.operatorValueList[index]?.operator,
        'valueType': this.selectedPremiumDataElement.operatorValueList[index]?.valueType,
        'customAttributeInfo': this.selectedPremiumDataElement.operatorValueList[index]?.customAttributeInfo,
        'values': this.selectedPremiumDataElement.operatorValueList[index]?.values,
      };
      this.premSearchFieldOptions.fieldCode = premSearchField.fieldCode;
      this.premSearchFieldOptions.operatorValues = this.premSearchFieldOptions?.operatorValues?.length ?
        this.premSearchFieldOptions.operatorValues : [operatorValueIsItem];
      if (event.target.checked) {
        if (!(this.premSearchFieldOptions.operatorValues[0]?.values)) {
          this.premSearchFieldOptions.operatorValues[0].values = [option.code];
        } else {
          this.premSearchFieldOptions.operatorValues[0].values.push(option.code || '');
        }
      } else {
        const indexE = this.premSearchFieldOptions?.operatorValues[0]?.values?.length &&
          this.premSearchFieldOptions.operatorValues[0].values.indexOf(option.code);
        if (indexE !== -1) {
          this.premSearchFieldOptions.operatorValues[0].values.splice(indexE, 1);
        }
      }
    } else {
      this.premSearchFieldOptions = this.premSearchFieldOptions.operatorValues ? this.premSearchFieldOptions : premSearchField;
      let values: Array<string>, index: number;
      if (this.selectedPremiumDataElement.fieldCode === 'PREM_HOME_EQUITY' ||
        this.selectedPremiumDataElement.fieldCode === 'PREM_LAST_SALE_PRICE' ||
        this.selectedPremiumDataElement.fieldCode === 'PREM_PROPERTY_VALUE') {

        if ((option.code.indexOf('-') !== -1)) {
          index = 1;
          values = option.code.split('-').map((item) => {
            return item
              .replace(/\$/g, '')
              .replace(/,/g, '')
              .replace(/k/g, '000')
              .replace(/mm/g, '000000');
          });
        } else if (option.code.indexOf('Less than') !== -1) {
          index = PremiumMapSearchFieldConstants.INDEX_THREE;
          values = [option.code.replace('Less than ', '')];
        } else if (option.code.indexOf('+') !== -1) {
          index = PremiumMapSearchFieldConstants.INDEX_TWO;
          values = [option.code.replace(/\$/g, '').replace(/\+/g, '').replace(/,/g, '')];
        }
      } else if (this.selectedPremiumDataElement.fieldCode === 'PREM_RENT_SCORE' ||
        this.selectedPremiumDataElement.fieldCode === 'PREM_SELL_SCORE') {
        if ((option.code.indexOf('Not Available') === -1) &&
          (option.code.indexOf('Yes') === -1) &&
          (option.code.indexOf('No') === -1)) {
          index = PremiumMapSearchFieldConstants.INDEX_ONE;
          values = option.code.split('score of ')[1].split(')')[0].split('-');
        } else {
          index = PremiumMapSearchFieldConstants.INDEX_ZERO;
          values = [option.code];
        }
      } else if (this.selectedPremiumDataElement.fieldCode === 'PREM_MORTGAGE_RATE') {
        if (option.code.indexOf('- ') !== -1) {
          const splitArr = option.code.split('- ');
          index = PremiumMapSearchFieldConstants.INDEX_ONE;
          const item1 = splitArr[0].split('%')[0] === '0.0' ?
            splitArr[0].split('%')[0] :
            JSON.stringify(parseFloat(splitArr[0].split('%')[0]) + parseFloat('0.001'));
          const item2 = splitArr[1].split('%')[0];
          values = [item1, item2];
        } else if (option.code.indexOf('+') !== -1) {
          index = PremiumMapSearchFieldConstants.INDEX_TWO;
          values = [option.code.split('+')[0].split('%')[0]];
        } else {
          index = PremiumMapSearchFieldConstants.INDEX_ZERO;
          values = [option.code];
        }
      } else if (this.selectedPremiumDataElement.fieldCode === 'PREM_MORTGAGE_AGE' ||
        this.selectedPremiumDataElement.fieldCode === 'PREM_OWNERSHIP_TIME') {
        if ((option.code.indexOf('-') !== -1)) {
          index = PremiumMapSearchFieldConstants.INDEX_ONE;
          const item1: string = JSON.stringify(parseInt(
            option.code.split('-')[PremiumMapSearchFieldConstants.INDEX_ZERO] === '0' ?
              option.code.split('-')[PremiumMapSearchFieldConstants.INDEX_ZERO] :
              option.code.split('-')[PremiumMapSearchFieldConstants.INDEX_ZERO]
                .split('(')[PremiumMapSearchFieldConstants.INDEX_ZERO], 10
          ) * PremiumMapSearchFieldConstants.NO_OF_DAYS_PER_YEAR);
          const item2: string = JSON.stringify(parseInt(
            option.code.split('-')[1].split(' years')[PremiumMapSearchFieldConstants.INDEX_ZERO], 10
          ) * PremiumMapSearchFieldConstants.NO_OF_DAYS_PER_YEAR);
          values = [item1, item2];
        } else if (option.code.indexOf('+') !== -1) {
          index = 2;
          values = [`${+option.code.split('+')[0] * PremiumMapSearchFieldConstants.NO_OF_DAYS_PER_YEAR + 1}`];
        } else {
          index = 0;
          values = [option.code];
        }
      } else if (this.selectedPremiumDataElement.fieldCode === 'PREM_OWNER_OCCUPIED') {
        index = (option.code === 'Y' ? PremiumMapSearchFieldConstants.INDEX_ONE :
          (option.code === 'N' ? PremiumMapSearchFieldConstants.INDEX_TWO :
            PremiumMapSearchFieldConstants.INDEX_THREE)); // option.code === 'U'
        values = [option.code];
      }

      values = values.map((i) => i.trim());

      const operatorValueItem = {
        'operatorLabel': this.selectedPremiumDataElement.operatorValueList[index]?.operatorLabel,
        'operator': this.selectedPremiumDataElement.operatorValueList[index]?.operator,
        'valueType': this.selectedPremiumDataElement.operatorValueList[index]?.valueType,
        'customAttributeInfo': this.selectedPremiumDataElement.operatorValueList[index]?.customAttributeInfo,
        'values': values,
        lookUpIndex: option.lookUpIndex
      };

      if (this.premSearchFieldOptions.operatorValues) {
        if (event.target.checked) {
          this.premSearchFieldOptions.operatorValues.push(operatorValueItem);
        } else {
          this.premSearchFieldOptions.operatorValues = this.premSearchFieldOptions.operatorValues
            .filter((item: any) => {
              return operatorValueItem.values[0] !== item.values[0];
            });
        }
      } else {
        this.premSearchFieldOptions.operatorValues = [operatorValueItem];
      }
    }

    if (isToggledSingle) {
      this.toggleVal.emit(this.premSearchFieldOptions);

      this.lookups = this.lookups.map((lookup) => {
        if (lookup.code === option.code) {
          return {
            ...lookup,
            selected: !lookup.selected
          };
        } else {
          return lookup;
        }
      });
      this.store$.dispatch(new SavedPremiumLayerLookups(this.lookups));
    }

    const selectedLayer = this.premiumLayers.find((item) => item.fieldCode === this.premSearchFieldOptions.fieldCode);
    this.store$.dispatch(new SetPremiumLayer(selectedLayer, this.getSelectedLookups()));
  }

  toggleAll(event: any) {
    this.isToggleAllChecked = event.target.checked;
    this.store$.dispatch(new ClearAllPremium(false));
    if (event.target.checked) {
      this.premSearchFieldOptions = {};
      this.lookups = this.lookups.map((option) => {
        const obj = {
          ...option,
          selected: true
        };
        this.togglePremiumOption(event, obj, false);
        return obj;
      });
    } else {
      this.lookups = this.lookups.map((option) => {
        this.togglePremiumOption(event, option, false);
        return {
          ...option,
          selected: false
        };
      });
      this.premSearchFieldOptions.operatorValues = [];
    }
    this.toggleVal.emit(this.premSearchFieldOptions);
    const selectedLayer = this.premiumLayers.find((item) => item.fieldCode === this.premSearchFieldOptions.fieldCode);
    this.store$.dispatch(new SetPremiumLayer(selectedLayer, this.getSelectedLookups()));
    // Build out the state for each layer so the back button works.
    this.store$.dispatch(new SavedPremiumLayerLookups(this.lookups));
  }

  private getSelectedLookups() {
    return this.lookups.reduce((lookups, lookup) => {
      if (lookup.selected) {
        lookups.push(lookup.code);
      }
      return lookups;
    }, []);
  }
}
